import { useMemo, useState } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { MapPinned } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../components/ui/chart';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';

const chartConfig = {
  sales: {
    label: 'Sales',
    color: '#01796d',
  },
  orders: {
    label: 'Orders',
    color: '#64748b',
  },
};

const salesColor = '#01796d';
const ordersColor = '#64748b';

export function AreaSalesChart({ data }) {
  const [range, setRange] = useState('12');
  const safeData = Array.isArray(data) ? data : [];

  const processedData = useMemo(() => {
    const activeAreas = safeData
      .map((item) => ({
        area: item.area || item.AreaName || 'Unknown',
        orders: item.orders || item.OrderCount || 0,
        sales: item.sales || item.TotalSales || 0,
        percentage: item.percentage || item.Percentage || 0,
      }))
      .filter((item) => item.orders > 0 || item.sales > 0)
      .sort((a, b) => b.sales - a.sales);

    if (range === 'all') {
      return activeAreas;
    }

    return activeAreas.slice(0, Number(range));
  }, [safeData, range]);

  const stats = useMemo(() => {
    const allActiveAreas = safeData
      .map((item) => ({
        area: item.area || item.AreaName || 'Unknown',
        orders: item.orders || item.OrderCount || 0,
        sales: item.sales || item.TotalSales || 0,
      }))
      .filter((item) => item.orders > 0 || item.sales > 0);

    const totalSales = allActiveAreas.reduce((sum, item) => sum + (item.sales || 0), 0);
    const totalOrders = allActiveAreas.reduce((sum, item) => sum + (item.orders || 0), 0);
    const topArea = allActiveAreas.sort((a, b) => b.sales - a.sales)[0];

    return {
      totalSales,
      totalOrders,
      activeAreas: allActiveAreas.length,
      topArea: topArea?.area || 'N/A',
    };
  }, [safeData]);

  return (
    <Card className="pt-0 rounded-2xl shadow-lg border-slate-200 hover:shadow-xl transition-all">
      <CardHeader className="flex items-center gap-2 space-y-0 py-5 py-0 sm:flex-row">
        <div className="grid flex-1 gap-1">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-[#01796d] to-teal-600 p-3 rounded-xl shadow-lg">
              <MapPinned className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg font-bold text-slate-900">Area Sales</CardTitle>
              <CardDescription>Sales and orders by area</CardDescription>
            </div>
          </div>
        </div>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger
            className="hidden w-[160px] rounded-lg bg-slate-50 sm:ml-auto sm:flex"
            aria-label="Select area range"
          >
            <SelectValue placeholder="Top 12 areas" />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            <SelectItem value="12" className="rounded-lg">
              Top 12 areas
            </SelectItem>
            <SelectItem value="25" className="rounded-lg">
              Top 25 areas
            </SelectItem>
            <SelectItem value="all" className="rounded-lg">
              All active areas
            </SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>

      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        {processedData.length === 0 ? (
          <div className="h-[320px] flex items-center justify-center">
            <div className="text-center">
              <MapPinned className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No area sales data available</p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="p-3 bg-[#01796d]/10 rounded-xl">
                <p className="text-xs text-slate-600 mb-1">Top Area</p>
                <p className="text-xl font-bold text-[#01796d] truncate">{stats.topArea}</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-xl">
                <p className="text-xs text-slate-600 mb-1">Total Sales</p>
                <p className="text-xl font-bold text-slate-800">
                  Rs. {stats.totalSales.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div className="p-3 bg-slate-100 rounded-xl">
                <p className="text-xs text-slate-600 mb-1">Total Orders</p>
                <p className="text-xl font-bold text-slate-800">{stats.totalOrders.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-xl">
                <p className="text-xs text-slate-600 mb-1">Active Areas</p>
                <p className="text-xl font-bold text-slate-800">{stats.activeAreas}</p>
              </div>
            </div>

            <ChartContainer
              config={chartConfig}
              className="aspect-auto h-[300px] w-full"
            >
              <AreaChart data={processedData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="areaSalesFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={salesColor} stopOpacity={0.42} />
                    <stop offset="95%" stopColor={salesColor} stopOpacity={0.04} />
                  </linearGradient>
                  <linearGradient id="areaOrdersFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={ordersColor} stopOpacity={0.24} />
                    <stop offset="95%" stopColor={ordersColor} stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="area"
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickMargin={8}
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  interval={0}
                  angle={-18}
                  textAnchor="end"
                  height={65}
                />
                <YAxis
                  yAxisId="left"
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  tickFormatter={(value) => value.toLocaleString()}
                  label={{ value: 'Sales (Rs)', angle: -90, position: 'insideLeft', fontSize: 10 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  tickFormatter={(value) => value.toLocaleString()}
                  label={{ value: 'Orders', angle: 90, position: 'insideRight', fontSize: 10 }}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      indicator="dot"
                      formatter={(value, name) => {
                        if (name === 'sales') {
                          return [`Rs. ${Number(value || 0).toLocaleString()}`, 'Sales'];
                        }
                        return [Number(value || 0).toLocaleString(), 'Orders'];
                      }}
                    />
                  }
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="sales"
                  stroke={salesColor}
                  strokeWidth={3}
                  fill="url(#areaSalesFill)"
                  name="Sales"
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 2, stroke: '#ffffff' }}
                  connectNulls
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="orders"
                  stroke={ordersColor}
                  strokeWidth={2}
                  fill="url(#areaOrdersFill)"
                  name="Orders"
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 2, stroke: '#ffffff' }}
                  connectNulls
                />
              </AreaChart>
            </ChartContainer>

            <div className="mt-4 text-center">
              <p className="text-xs text-slate-400">
                Showing top active areas by sales in the selected range
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
